import { useState } from 'react';
import { buildApiUrl } from '../../config/api';

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  origen?: string;
}

const PERFILES = [
  'Guía autónomo',
  'Guía con equipo',
  'Agencia receptiva',
  'Empresa de tours',
  'Otra cosa'
];

/**
 * "Que me llamen", desde dentro de la demostración.
 *
 * Va al mismo sitio que cualquier otro lead (POST /api/demo/lead), cambiando
 * solo el `origen`: así se puede separar después quién pidió precio DESPUÉS de
 * haber probado el producto, que es la conversión que de verdad interesa medir.
 */
export default function DemoLeadModal({ abierto, onCerrar, origen = 'demo' }: Props) {
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!abierto) return null;

  const enviar = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setEnviando(true);

    const campos = new FormData(e.currentTarget);
    try {
      const res = await fetch(buildApiUrl('/api/demo/lead'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: campos.get('nombre'),
          email: campos.get('email'),
          telefono: campos.get('telefono'),
          perfil: campos.get('perfil'),
          mensaje: campos.get('mensaje'),
          origen,
          // Trampa para robots: invisible, solo la rellenan ellos.
          website: campos.get('website')
        })
      });

      if (!res.ok) throw new Error(String(res.status));
      setEnviado(true);
    } catch {
      setError('No hemos podido enviarlo. Inténtalo otra vez en un momento.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
        {enviado ? (
          <div className="text-center">
            <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
              Recibido
            </h2>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Te escribimos en menos de 24 horas. Mientras tanto puedes seguir
              dando vueltas por la demostración.
            </p>
            <button
              type="button"
              onClick={onCerrar}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Seguir mirando
            </button>
          </div>
        ) : (
          <form onSubmit={enviar}>
            <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
              ¿Lo montamos con tus tours?
            </h2>
            <p className="mb-5 text-sm text-gray-600 dark:text-gray-400">
              Acabas de hacerlo con una guía de ejemplo. Con tus clientes y tus
              tarifas se configura en una tarde.
            </p>

            <div className="space-y-3">
              <input
                name="nombre"
                required
                minLength={2}
                maxLength={120}
                placeholder="Tu nombre"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
              <input
                name="email"
                type="email"
                required
                maxLength={255}
                placeholder="Tu correo"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
              <input
                name="telefono"
                type="tel"
                maxLength={32}
                placeholder="Teléfono (opcional)"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
              <select
                name="perfil"
                defaultValue=""
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              >
                <option value="">¿Qué eres? (opcional)</option>
                {PERFILES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <textarea
                name="mensaje"
                rows={3}
                maxLength={2000}
                placeholder="¿Algo que quieras contarnos? (opcional)"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />

              {/* Trampa para robots. Invisible y fuera del tabulador. */}
              <input
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
            </div>

            {error && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}

            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={onCerrar}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Ahora no
              </button>
              <button
                type="submit"
                disabled={enviando}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {enviando ? 'Enviando…' : 'Enviar'}
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
              Sin compromiso. Esto no crea ninguna cuenta.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
